import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";

import { useEnableOrbac } from "featureFlags/hooks/useEnableOrbac";
import { authService } from "services/auth";
import { SqlRbacAction } from "services/rbac/action";
import { getOrganizationPrivileges } from "services/rbac/getOrganizationPrivileges";
import { getOwnedAccountObjectsPrivileges } from "services/rbac/getOwnedAccountObjectsPrivileges";
import { getOwnedOrgObjectsPrivileges } from "services/rbac/getOwnedOrgObjectsPrivileges";
import { getPrivileges } from "services/rbac/getPrivileges";
import { Privilege, PrivilegeObjectType } from "services/rbac/types";
import { getUserByLogin } from "services/users/getUserByLogin";

import FakePageLoader from "../App/FakePageLoader";
import { accessManager } from "../App/accessManager";

interface Props {
  children: React.ReactNode;
  currentAccountName: string;
}

const PrivilegesLoader = (props: Props) => {
  const { children, currentAccountName } = props;
  const orbacEnabled = useEnableOrbac();
  const [isLoadingPrivileges, setIsLoadingPrivileges] = useState(true);

  useEffect(() => {
    const load = async () => {
      setIsLoadingPrivileges(true);
      try {
        const auth0user = await authService.auth0client.getUser();
        const loginName = auth0user?.email ?? "";

        const user = await getUserByLogin(loginName);

        const privilegeRequests = await Promise.allSettled([
          getPrivileges(loginName),
          getOwnedAccountObjectsPrivileges(user.userName),
          new Promise<Privilege[]>(resolve => {
            if (!orbacEnabled) {
              resolve([]);
              return;
            }
            resolve(getOrganizationPrivileges(loginName));
          }),
          new Promise<Privilege[]>(resolve => {
            if (!orbacEnabled) {
              resolve([]);
              return;
            }
            resolve(getOwnedOrgObjectsPrivileges(loginName));
          }),
        ]);

        const privilegesList = privilegeRequests.map(res => {
          if (res.status === "rejected") {
            Sentry.captureException(new Error("Error fetching privileges"), {
              extra: {
                error: res.reason,
              },
            });
            console.error("Error fetching privileges", res.reason);
          }

          // for failed requests, return an empty array
          return "value" in res ? res.value : [];
        });

        const systemEnginePrivilege = {
          objectType: PrivilegeObjectType.engine,
          objectName: "system",
          privilegeType: SqlRbacAction.USAGE,
        };

        if (!authService.organizationName) {
          throw new Error("Organization name is not defined in authService");
        }

        await accessManager.initAccessManager(
          [...privilegesList.flat(), systemEnginePrivilege],
          currentAccountName,
          authService.organizationName,
          user
        );
      } catch (error) {
        Sentry.captureException(new Error("Error loading privileges"), {
          extra: {
            error,
          },
        });
        console.error("Error loading privileges", error);
      } finally {
        setIsLoadingPrivileges(false);
      }
    };

    load();
  }, [currentAccountName, orbacEnabled]);

  if (isLoadingPrivileges) {
    return <FakePageLoader title="Finalizing configuration..." />;
  }

  return children;
};

export default PrivilegesLoader;
